// Hooks + helpers that pull holiday + break-rule data for the calendar views
// and aggregate per-day metrics (worked, breaks taken, required pause, holiday).
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
// Kanonische Saldo-Lib (packages/shared) — direkt als TS konsumiert. @embertime/
// shared ist im web-Paket (noch) nicht als Dependency verdrahtet, deshalb der
// relative Pfad auf die reine, DB-freie Quelle.
import {
  dayActualMinutes,
  type EntryLike,
  type Interval,
} from '../../../../packages/shared/src/saldo'
import {
  type Absence,
  api,
  type Break,
  type BreakRule,
  type BreakRuleTier,
  type Nonbusinessday,
  type TimeEntry,
  type User,
  type WorkSchedule,
} from './api'
import { requiredBreakMinutes, tiersOf } from './break-rules'
import { dayKey } from './calendar'

export function useHolidaysInRange(
  user: { nonbusinessgroupId: string | null } | null | undefined,
  fromIso: string,
  toIso: string,
) {
  const groupId = user?.nonbusinessgroupId ?? null
  const { data } = useQuery({
    queryKey: ['nonbusinessdays', { groupId, fromIso, toIso }],
    queryFn: () =>
      api.listNonbusinessdays({ groupId: groupId ?? undefined, from: fromIso, to: toIso }),
    enabled: !!groupId,
  })
  return useMemo(() => {
    const map = new Map<string, Nonbusinessday>()
    for (const d of data?.days ?? []) {
      map.set(dayKey(new Date(d.date)), d)
    }
    return map
  }, [data])
}

export function useBreaksInRange(
  user: User | null | undefined,
  fromIso: string,
  toIso: string,
  /** Override for view-as: when set, fetches breaks for that user instead
   * of the session's user. Caller is responsible for permission checks. */
  viewAsUserId?: string,
) {
  const { data } = useQuery({
    queryKey: ['breaks', { fromIso, toIso, viewAsUserId }],
    queryFn: () => api.listBreaks({ from: fromIso, to: toIso, userId: viewAsUserId }),
    enabled: !!user,
  })
  return data?.breaks ?? []
}

// Per-day target hours, driven by the user's work_schedules.
// work_schedules are time-period-based (valid_from/valid_to) with Mo–So
// individual hour fields. The active schedule for a given date wins; if
// none applies (user has no configured schedule for that period) the
// daily target is 0.
//
// Single source of truth: same table the Saldo-Berechnung in
// /api/time-accounts reads from. No more drift between Kalender-Anzeige
// und Stundenkonto.

export function useWorkSchedulesForUser(user: User | null | undefined, viewAsUserId?: string) {
  const { data } = useQuery({
    queryKey: ['work-schedules', viewAsUserId ?? 'self'],
    queryFn: () => api.listWorkSchedules(viewAsUserId),
    enabled: !!user,
  })
  return data?.schedules ?? []
}

const WEEKDAY_FIELDS = [
  'sunHours',
  'monHours',
  'tueHours',
  'wedHours',
  'thuHours',
  'friHours',
  'satHours',
] as const

function activeScheduleForDate(day: Date, schedules: readonly WorkSchedule[]): WorkSchedule | null {
  const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
  let best: WorkSchedule | null = null
  for (const s of schedules) {
    if (s.validFrom <= dateStr && (s.validTo == null || s.validTo >= dateStr)) {
      if (!best || s.validFrom > best.validFrom) best = s
    }
  }
  return best
}

export function targetMinutesForDay(day: Date, schedules: readonly WorkSchedule[]): number {
  const s = activeScheduleForDate(day, schedules)
  if (!s) return 0
  const hours = Number(s[WEEKDAY_FIELDS[day.getDay()]])
  if (!Number.isFinite(hours) || hours <= 0) return 0
  return Math.round(hours * 60)
}

export function useActiveBreakRule(
  user: { breakRuleId: string | null } | null | undefined,
): BreakRule | null {
  const id = user?.breakRuleId ?? null
  const { data } = useQuery({
    queryKey: ['break-rules'],
    queryFn: () => api.listBreakRules(),
    enabled: !!user,
  })
  return useMemo(() => {
    if (!data) return null
    if (id) return data.rules.find((r) => r.id === id) ?? null
    // No explicit choice → DE federal default
    return data.rules.find((r) => r.country === 'DE' && r.region === null) ?? null
  }, [data, id])
}

// Per-day aggregation. Time-entries and breaks are clipped to the day's
// [00:00, 24:00] window, so an entry crossing midnight contributes to both
// days proportionally.
//
// German ArbZG §4 has TWO independent rules and we evaluate both:
//   1. Daily total: ≥6h worked → ≥30min break, ≥9h worked → ≥45min break.
//      Breaks may be split into chunks of ≥15min each.
//   2. Continuity: nobody may work more than 6h "hintereinander" without a
//      break of ≥15min. So even if total breaks satisfy rule 1, an
//      uninterrupted stretch of >6h is itself a violation.
//
// `takenBreakMinutes` counts explicit `Break` records and gaps ≥15min
// between entries (the gap *is* a pause for ArbZG purposes). `longestRunMinutes`
// is the longest stretch of work uninterrupted by a ≥15min pause.
export interface DayMetrics {
  workedMinutes: number
  takenBreakMinutes: number
  requiredBreakMinutes: number
  /** Positive when the daily total rule (#1) is violated (required > taken). */
  breakDeficitMinutes: number
  /** Longest continuous work stretch interrupted only by gaps <15min. */
  longestRunMinutes: number
  /** True when the continuity rule (#2) is violated (longestRun > 6h). */
  unbrokenStretchViolation: boolean
  holiday: Nonbusinessday | null
  /** Set when the day falls inside a vacation/sick/training/other absence. */
  absence: Absence | null
  /** Pending-Antrag am Tag (nur wenn KEINE approved-Absence greift) — rein
   *  fürs „ausstehend"-Styling; kreditiert nie das Soll. */
  pendingAbsence: Absence | null
  /** True when an absence covers the day AND time entries also exist on it. */
  entriesOnAbsence: boolean
  /** Soll-Stunden in Minuten für diesen Tag aus dem User-Wochenplan.
   *  0 wenn der Wochentag kein Soll hat (Wochenende/Pre-Hire/Post-Exit). */
  targetMinutes: number
  /** Minutes aus Meetings, die NICHT durch einen überlappenden Time-Entry
   *  abgedeckt sind. So zählen ad-hoc-Meetings die der User vergessen hat
   *  als Eintrag zu erfassen trotzdem zur Ist-Seite. */
  meetingMinutes: number
  /** Ist-Total für den Soll-Ist-Abgleich:
   *   - Absence-Tag:  targetMinutes  (Urlaub/Krank/Fortbildung erfüllt das Soll)
   *   - Sonstige:     workedMinutes + meetingMinutes
   *  Wenn targetMinutes=0 wird trotzdem das Ist gezeigt (positiver Überschuss). */
  totalActualMinutes: number
  /** Beginn des ersten Arbeitssegments des Tages (epoch ms, auf den Tag
   *  geclippt). null wenn der Tag keine Einträge hat. Meetings zählen bewusst
   *  NICHT mit — der Footer zeigt den Arbeitstag aus Zeiteinträgen. */
  firstStartMs: number | null
  /** Ende des letzten Arbeitssegments (epoch ms, geclippt — ein Eintrag über
   *  Mitternacht endet hier auf der lokalen Folgetags-Mitternacht). */
  lastEndMs: number | null
  /** True wenn lastEndMs von einem noch laufenden Eintrag stammt („jetzt"),
   *  nicht von einem festen Arbeitsende. */
  hasRunningEntry: boolean
}

const MIN_IMPLICIT_GAP_MINUTES = 15
const MAX_UNBROKEN_MINUTES = 6 * 60

function findAbsence(day: Date, absences: Absence[]): Absence | null {
  // Compare on date-only (start-of-day) so a vacation 06.05–08.05 covers all
  // three days inclusive regardless of stored timestamps.
  //
  // Only APPROVED absences credit the day's Soll. pending/rejected/withdrawn/
  // cancelled requests must NOT count as fulfilled target — otherwise the
  // calendar shows Diff 0 for a day the Stundenkonto-Backend
  // (time-accounts.ts: `filter(a => a.status === 'approved')`) treats as a
  // normal work day, and the two engines drift by up to a full day's target.
  const d0 = new Date(day)
  d0.setHours(0, 0, 0, 0)
  for (const a of absences) {
    if (a.status !== 'approved') continue
    const from = new Date(a.fromDate)
    from.setHours(0, 0, 0, 0)
    const to = new Date(a.toDate)
    to.setHours(23, 59, 59, 999)
    if (d0 >= from && d0 <= to) return a
  }
  return null
}

/** Pending-Abwesenheit des Tages — fürs Anzeige-Styling (Streifen +
 *  „ausstehend"), NICHT für den Saldo (der kreditiert nur approved).
 *  rejected/withdrawn/cancelled zählen bewusst gar nicht. */
function findPendingAbsence(day: Date, absences: Absence[]): Absence | null {
  const d0 = new Date(day)
  d0.setHours(0, 0, 0, 0)
  for (const a of absences) {
    if (a.status !== 'pending') continue
    const from = new Date(a.fromDate)
    from.setHours(0, 0, 0, 0)
    const to = new Date(a.toDate)
    to.setHours(23, 59, 59, 999)
    if (d0 >= from && d0 <= to) return a
  }
  return null
}

/**
 * Anzeige-Abwesenheit eines Tages für die Kalender-Renderer: approved
 * gewinnt, sonst pending (mit Flag) — abgelehnte/zurückgezogene färben
 * nicht mehr. Der Saldo-Pfad (metricsForDay.absence) bleibt approved-only.
 */
export function displayAbsenceForDay(
  day: Date,
  absences: Absence[],
): { absence: Absence; pending: boolean } | null {
  const approved = findAbsence(day, absences)
  if (approved) return { absence: approved, pending: false }
  const pending = findPendingAbsence(day, absences)
  return pending ? { absence: pending, pending: true } : null
}

// Fraction (0.5 for half-day, 1.0 for full) of the day covered by the given
// absence — mirrors the backend's `absenceDayFraction` in time-accounts.ts so
// a half vacation day credits half the target and the other half must still be
// worked. Single-day absences use only firstDayHalf; multi-day spans mark the
// first day via firstDayHalf and the last day via lastDayHalf.
function absenceFractionForDay(day: Date, a: Absence): number {
  const fromKey = dayKey(new Date(a.fromDate))
  const toKey = dayKey(new Date(a.toDate))
  const k = dayKey(day)
  if (fromKey === toKey) return a.firstDayHalf ? 0.5 : 1
  if (k === fromKey) return a.firstDayHalf ? 0.5 : 1
  if (k === toKey) return a.lastDayHalf ? 0.5 : 1
  return 1
}

// Union-merge half-open [startMs, endMs) intervals so overlapping spans count
// once. Used for both the ArbZG pause total (explicit breaks ∪ inter-entry
// gaps) and the un-covered meeting minutes.
function mergeIntervals(
  intervals: { startMs: number; endMs: number }[],
): { startMs: number; endMs: number }[] {
  if (intervals.length === 0) return []
  const sorted = [...intervals].sort((a, b) => a.startMs - b.startMs)
  const out = [{ ...sorted[0] }]
  for (let i = 1; i < sorted.length; i++) {
    const last = out[out.length - 1]
    if (sorted[i].startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, sorted[i].endMs)
    } else {
      out.push({ ...sorted[i] })
    }
  }
  return out
}

export function metricsForDay(
  day: Date,
  entries: TimeEntry[],
  breaks: Break[],
  tiers: BreakRuleTier[],
  holidays: Map<string, Nonbusinessday>,
  absences: Absence[] = [],
  targetHours: readonly WorkSchedule[] = [],
  meetings: { startTime: string; endTime: string; countsAsWork?: boolean }[] = [],
): DayMetrics {
  const k = dayKey(day)
  const absence = findAbsence(day, absences)
  const pendingAbsence = absence ? null : findPendingAbsence(day, absences)
  const holiday = holidays.get(k) ?? null
  // Feiertag senkt das Tagessoll exakt wie die Saldo-Engine im Backend
  // (time-accounts.ts: ganzer Feiertag → 0, halber → halbes Soll). Ohne diese
  // Spiegelung zeigte der Tages-Diff an arbeitsfreien Feiertagen ein Phantom-
  // Defizit (z.B. −8:00), obwohl kein Soll zu erfüllen ist.
  const rawTarget = targetMinutesForDay(day, targetHours)
  const targetMinutes = holiday ? (holiday.halfDay ? Math.round(rawTarget * 0.5) : 0) : rawTarget

  const dayStart = new Date(day)
  dayStart.setHours(0, 0, 0, 0)
  const dayStartMs = dayStart.getTime()
  // Day end = LOCAL midnight of the next day, not +24h. On DST-transition days
  // the local day is 23h or 25h long; a fixed +24h window either swallows an
  // hour of the following day (23h spring day) or drops a 23:00 entry that
  // lives past dayStart+24h but before the real next midnight (25h autumn day).
  const dayEnd = new Date(dayStart)
  dayEnd.setDate(dayEnd.getDate() + 1)
  const dayEndMs = dayEnd.getTime()

  // Time-entry segments clipped to this day, sorted by start. `segments` feeds
  // die ArbZG-Pausen-/Kontinuitätslogik unverändert weiter; `entryLikes` (mit
  // per-Tag zugeordneter Anfahrt/Abfahrt) füttert die kanonische Ist-Lib.
  // Anfahrt/Abfahrt leben nur auf dem Start-/End-Tag des Eintrags, damit ein
  // mehrtägiger Eintrag die Anfahrt an Tag-1 und die Abfahrt an Tag-N gutschreibt.
  const segments: { startMs: number; endMs: number }[] = []
  const entryLikes: EntryLike[] = []
  // Ende des spätesten Segments, das von einem LAUFENDEN Eintrag stammt und
  // NICHT an der Tagesgrenze geclippt wurde (segE === „jetzt"). Nur dann ist
  // das Arbeitsende des Tages offen („läuft") statt fest.
  let runningEndMs: number | null = null
  for (const e of entries) {
    const s = new Date(e.startTime).getTime()
    const en = e.endTime ? new Date(e.endTime).getTime() : Date.now()
    const segS = Math.max(s, dayStartMs)
    const segE = Math.min(en, dayEndMs)
    if (segE <= segS) continue
    segments.push({ startMs: segS, endMs: segE })
    if (!e.endTime && segE === en && (runningEndMs == null || segE > runningEndMs)) {
      runningEndMs = segE
    }
    entryLikes.push({
      start: segS,
      end: segE,
      // Reisezeit pro Eintrag (travelCountsAsWork) — und nur am echten
      // Start-/End-Tag des Eintrags (mehrtägige Einträge kriegen die Anfahrt
      // an Tag 1, die Abfahrt an Tag N).
      travelToMin:
        e.travelCountsAsWork && s >= dayStartMs && s < dayEndMs ? e.travelTimeToMinutes : 0,
      // Abfahrt erst wenn der Eintrag beendet ist — ein laufender Timer hat
      // noch kein Arbeitsende (Konsistenz zu weekly/range/team-year).
      travelFromMin:
        e.travelCountsAsWork && e.endTime && en > dayStartMs && en <= dayEndMs
          ? e.travelTimeFromMinutes
          : 0,
    })
  }
  segments.sort((a, b) => a.startMs - b.startMs)

  // Arbeitstag-Grenzen für die Stundenzettel-Zeile im WeekFooter: erster
  // Segment-Start und spätestes Segment-Ende (NICHT segments.at(-1) — ein
  // umschließender Eintrag kann früher starten und später enden als der
  // zuletzt startende).
  const firstStartMs = segments.length > 0 ? segments[0].startMs : null
  const lastEndMs =
    segments.length > 0 ? segments.reduce((max, s) => Math.max(max, s.endMs), 0) : null
  const hasRunningEntry = runningEndMs != null && lastEndMs != null && runningEndMs >= lastEndMs

  // Meetings auf den Tag geclippt — die Union-Merge (untereinander + gegen die
  // Einträge) macht dayActualMinutes.
  const meetingIntervals: Interval[] = []
  for (const m of meetings) {
    // Nur Meetings mit countsAsWork zählen zur Ist-Zeit; undefined zählt
    // NICHT (defensiv gegen stale Caches ohne das neue Feld).
    if (!m.countsAsWork) continue
    const ms = new Date(m.startTime).getTime()
    const me = new Date(m.endTime).getTime()
    const segS = Math.max(ms, dayStartMs)
    const segE = Math.min(me, dayEndMs)
    if (segE > segS) meetingIntervals.push({ start: segS, end: segE })
  }

  // Kanonische Ist-Zeit über die Lib: Einträge werden union-gemergt (kein
  // Doppelzählen überlappender Einträge). Anfahrt bzw. Meetings zählen NUR wenn
  // das jeweilige globale Setting an ist. Der Kalender zieht die Pflichtpause
  // NICHT vom Ist ab (deductBreaks:false) — das Defizit wird separat
  // ausgewiesen, nicht vom Ist subtrahiert.
  const worked = dayActualMinutes(entryLikes, {
    // Travel-Felder sind bereits pro Eintrag gegated.
    countTravel: true,
    countMeetings: false,
    deductBreaks: false,
  }).grossMin
  const withMeetings = dayActualMinutes(entryLikes, {
    meetings: meetingIntervals,
    countTravel: true,
    // meetingIntervals ist bereits auf countsAsWork vorgefiltert.
    countMeetings: true,
    deductBreaks: false,
  }).grossMin
  // Ist-Anteil aus Meetings, der nicht schon von einem Eintrag abgedeckt ist
  // (0 wenn das Meetings-Setting aus ist).
  const meetingMinutes = Math.max(0, withMeetings - worked)

  // Explicit break records (completed only) clipped to the day, sorted.
  const breakSegs: { startMs: number; endMs: number }[] = []
  for (const b of breaks) {
    if (!b.endTime) continue
    const bs = new Date(b.startTime).getTime()
    const be = new Date(b.endTime).getTime()
    const segS = Math.max(bs, dayStartMs)
    const segE = Math.min(be, dayEndMs)
    if (segE > segS) breakSegs.push({ startMs: segS, endMs: segE })
  }
  breakSegs.sort((a, b) => a.startMs - b.startMs)
  const longBreakSegs = breakSegs.filter(
    (b) => (b.endMs - b.startMs) / 60000 >= MIN_IMPLICIT_GAP_MINUTES,
  )

  // Total break time = explicit breaks ≥15min UNION gaps ≥15min between
  // entries. ArbZG §4 only credits pause chunks of at least 15 minutes towards
  // the daily requirement — shorter breaks (smoke breaks, micro-pauses) don't
  // count. An explicit Break record that lies inside an entry gap must NOT be
  // counted twice (a stopped entry + a logged pause is the common case), so we
  // union the two interval sets before summing instead of adding both totals.
  const pauseIntervals: { startMs: number; endMs: number }[] = [...longBreakSegs]
  for (let i = 1; i < segments.length; i++) {
    const gapStart = segments[i - 1].endMs
    const gapEnd = segments[i].startMs
    if ((gapEnd - gapStart) / 60000 >= MIN_IMPLICIT_GAP_MINUTES) {
      pauseIntervals.push({ startMs: gapStart, endMs: gapEnd })
    }
  }
  let taken = 0
  for (const p of mergeIntervals(pauseIntervals)) taken += (p.endMs - p.startMs) / 60000

  // Continuity rule (#2): walk segments and grow a running stretch. A stretch
  // breaks when an inter-entry gap ≥15min OR an explicit ≥15min break occurs
  // between (or overlapping the boundary of) the previous and next segment.
  let longestRun = 0
  if (segments.length > 0) {
    let runStart = segments[0].startMs
    let runEnd = segments[0].endMs
    for (let i = 1; i < segments.length; i++) {
      const cur = segments[i]
      const gap = (cur.startMs - runEnd) / 60000
      const breakOverlap = longBreakSegs.some((b) => b.endMs > runEnd && b.startMs < cur.startMs)
      if (gap >= MIN_IMPLICIT_GAP_MINUTES || breakOverlap) {
        longestRun = Math.max(longestRun, (runEnd - runStart) / 60000)
        runStart = cur.startMs
      }
      runEnd = cur.endMs
    }
    longestRun = Math.max(longestRun, (runEnd - runStart) / 60000)
  }

  const required = requiredBreakMinutes(worked, tiers)
  const deficit = Math.max(0, required - taken)

  const totalActual = Math.round(worked) + Math.round(meetingMinutes)

  // Vacation/sick/training/other. A FULL absence day is fully credited via the
  // user's weekday target and actual entries are ignored (you can't be on
  // vacation AND log work), break rules muted. A HALF-day absence credits only
  // half the target and the other half must still be worked — so the real
  // worked/meeting minutes DO count and a shortfall stays visible, mirroring
  // the backend `targetMinutes += dayTarget * (1 - absenceFraction)`.
  if (absence) {
    const fraction = absenceFractionForDay(day, absence)
    const hasEntries = segments.length > 0
    if (fraction >= 1) {
      return {
        workedMinutes: targetMinutes,
        takenBreakMinutes: 0,
        requiredBreakMinutes: 0,
        breakDeficitMinutes: 0,
        longestRunMinutes: 0,
        unbrokenStretchViolation: false,
        holiday,
        absence,
        pendingAbsence,
        entriesOnAbsence: hasEntries,
        targetMinutes,
        meetingMinutes: 0,
        totalActualMinutes: targetMinutes,
        firstStartMs,
        lastEndMs,
        hasRunningEntry,
      }
    }
    const credit = Math.round(targetMinutes * fraction)
    return {
      workedMinutes: Math.round(worked),
      takenBreakMinutes: Math.round(taken),
      requiredBreakMinutes: required,
      breakDeficitMinutes: Math.round(deficit),
      longestRunMinutes: Math.round(longestRun),
      unbrokenStretchViolation: longestRun > MAX_UNBROKEN_MINUTES,
      holiday,
      absence,
      pendingAbsence,
      entriesOnAbsence: hasEntries,
      targetMinutes,
      meetingMinutes: Math.round(meetingMinutes),
      totalActualMinutes: credit + totalActual,
      firstStartMs,
      lastEndMs,
      hasRunningEntry,
    }
  }

  return {
    workedMinutes: Math.round(worked),
    takenBreakMinutes: Math.round(taken),
    requiredBreakMinutes: required,
    breakDeficitMinutes: Math.round(deficit),
    longestRunMinutes: Math.round(longestRun),
    unbrokenStretchViolation: longestRun > MAX_UNBROKEN_MINUTES,
    targetMinutes,
    meetingMinutes: Math.round(meetingMinutes),
    totalActualMinutes: totalActual,
    holiday,
    absence: null,
    pendingAbsence,
    entriesOnAbsence: false,
    firstStartMs,
    lastEndMs,
    hasRunningEntry,
  }
}

export function tiersForUser(rule: BreakRule | null): BreakRuleTier[] {
  return tiersOf(rule)
}

// ── Zeitraum-Summen mit Zukunfts-Cap ────────────────────────────────────────

/** True wenn der Kalendertag NACH heute liegt (lokale Tagesgrenze — kein
 *  UTC-Vergleich, sonst Off-by-one am Abend in UTC+2). */
export function isFutureDay(day: Date, now: Date = new Date()): boolean {
  const todayEnd = new Date(now)
  todayEnd.setHours(23, 59, 59, 999)
  return day.getTime() > todayEnd.getTime()
}

export interface RangeSollIst {
  targetMinutes: number
  workedMinutes: number
  totalActualMinutes: number
  /** Aufschlüsselung für den SollIstStrip: Einträge/Meetings nur an Tagen
   *  ohne Abwesenheit, Credit = Tagessoll an Abwesenheitstagen. */
  entriesMinutes: number
  meetingMinutes: number
  absenceCreditMinutes: number
  arbzgViolation: boolean
}

/**
 * Summiert Tages-Metriken über einen Zeitraum mit Zukunfts-Cap: Tage NACH
 * heute tragen weder Soll noch Ist/Absence-Credit bei (Ticket „Sollstunden
 * tagesaktuell"). Das ist die universelle Backend-Regel aus /time-accounts
 * („Tage nach heute tragen NIE Soll bei") — bewusst OHNE isCurrentYear-Gate,
 * damit Wochen über die Jahresgrenze (KW 1) korrekt cappen. Heute zählt mit
 * vollem Tagessoll. Vorher summierten Kopf-Pill, WeekFooter und Mobile-Ampel
 * das volle Zeitraum-Soll und färbten die laufende Woche dauerhaft rot.
 */
export function sumMetricsUntilToday(
  days: Date[],
  metricsByDay: DayMetrics[],
  now: Date = new Date(),
): RangeSollIst {
  const out: RangeSollIst = {
    targetMinutes: 0,
    workedMinutes: 0,
    totalActualMinutes: 0,
    entriesMinutes: 0,
    meetingMinutes: 0,
    absenceCreditMinutes: 0,
    arbzgViolation: false,
  }
  days.forEach((d, i) => {
    if (isFutureDay(d, now)) return
    const m = metricsByDay[i]
    out.targetMinutes += m.targetMinutes
    out.workedMinutes += m.workedMinutes
    out.totalActualMinutes += m.totalActualMinutes
    if (m.absence) {
      out.absenceCreditMinutes += m.targetMinutes
    } else {
      out.entriesMinutes += m.workedMinutes
      out.meetingMinutes += m.meetingMinutes
    }
    if (m.unbrokenStretchViolation || m.breakDeficitMinutes > 0) out.arbzgViolation = true
  })
  return out
}
